import { Routes } from '@angular/router';
import { authGuard, canActivateLogin } from './core/auth/auth.guard';

export const APP_ROUTES: Routes = [
  {
    path: '',
    loadComponent: () => import('./domains/public/components/public-shell.component').then(c => c.PublicShellComponent),
    children: [
      {
        path: '',
        loadComponent: () => import('./domains/public/public-home.component').then(c => c.PublicHomeComponent),
        pathMatch: 'full',
      },
      {
        path: 'invest',
        loadComponent: () => import('./domains/public/public-invest.component').then(c => c.PublicInvestComponent),
      },
      {
        path: 'borrow',
        loadComponent: () => import('./domains/public/public-borrow.component').then(c => c.PublicBorrowComponent),
      },
      {
        path: 'borrow/products/whole-loan',
        data: { productKey: 'whole-loan' },
        loadComponent: () => import('./domains/public/public-borrow-product.component').then(c => c.PublicBorrowProductComponent),
      },
      {
        path: 'borrow/products/mezzanine',
        data: { productKey: 'mezzanine' },
        loadComponent: () => import('./domains/public/public-borrow-product.component').then(c => c.PublicBorrowProductComponent),
      },
      {
        path: 'borrow/products/bridge',
        data: { productKey: 'bridge' },
        loadComponent: () => import('./domains/public/public-borrow-product.component').then(c => c.PublicBorrowProductComponent),
      },
      {
        path: 'borrow/products/stabilisation',
        data: { productKey: 'stabilisation' },
        loadComponent: () => import('./domains/public/public-borrow-product.component').then(c => c.PublicBorrowProductComponent),
      },
      {
        path: 'company/about-us',
        data: { pageKey: 'about-us' },
        loadComponent: () => import('./domains/public/public-company-page.component').then(c => c.PublicCompanyPageComponent),
      },
      {
        path: 'company/partnerships',
        data: { pageKey: 'partnerships' },
        loadComponent: () => import('./domains/public/public-company-page.component').then(c => c.PublicCompanyPageComponent),
      },
      {
        path: 'company/contact-us',
        data: { pageKey: 'contact-us' },
        loadComponent: () => import('./domains/public/public-company-page.component').then(c => c.PublicCompanyPageComponent),
      },
      {
        path: 'insights',
        loadComponent: () => import('./domains/public/public-insights.component').then(c => c.PublicInsightsComponent),
      },
      {
        path: 'insights/:slug',
        loadComponent: () => import('./domains/public/public-insight-detail.component').then(c => c.PublicInsightDetailComponent),
      },
      {
        path: 'faqs',
        loadComponent: () => import('./domains/public/public-faqs.component').then(c => c.PublicFaqsComponent),
      },
      {
        path: 'risk-information',
        data: { pageKey: 'risk-information' },
        loadComponent: () => import('./domains/public/public-legal-page-route.component').then(c => c.PublicLegalPageRouteComponent),
      },
      {
        path: 'key-risks',
        data: { pageKey: 'key-risks' },
        loadComponent: () => import('./domains/public/public-legal-page-route.component').then(c => c.PublicLegalPageRouteComponent),
      },
      {
        path: 'privacy-policy',
        data: { pageKey: 'privacy-policy' },
        loadComponent: () => import('./domains/public/public-legal-page-route.component').then(c => c.PublicLegalPageRouteComponent),
      },
      {
        path: 'website-terms-conditions',
        data: { pageKey: 'website-terms-conditions' },
        loadComponent: () => import('./domains/public/public-legal-page-route.component').then(c => c.PublicLegalPageRouteComponent),
      },
      {
        path: 'pillar-3',
        data: { pageKey: 'pillar-3' },
        loadComponent: () => import('./domains/public/public-legal-page-route.component').then(c => c.PublicLegalPageRouteComponent),
      },
      {
        path: 'glossary',
        data: { pageKey: 'glossary' },
        loadComponent: () => import('./domains/public/public-legal-page-route.component').then(c => c.PublicLegalPageRouteComponent),
      },
    ],
  },
  {
    path: 'login',
    loadComponent: () => import('./domains/login/login.component').then(c => c.LoginComponent),
    canActivate: [canActivateLogin],
  },
  {
    path: 'reset-password',
    loadComponent: () => import('./domains/login/reset-password/reset-password.component').then(c => c.ResetPasswordComponent),
    canActivate: [canActivateLogin],
  },
  {
    path: 'client',
    canActivateChild: [authGuard],
    data: { requiredRole: 'Client' },
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      { path: 'dashboard', data: { kycRequired: true }, loadComponent: () => import('./domains/client/client-dashboard.component').then(c => c.ClientDashboardComponent) },
      { path: 'deal/:id', data: { kycRequired: true }, loadComponent: () => import('./domains/client/deal-detail.component').then(c => c.DealDetailComponent) },
      { path: 'portfolio', data: { kycRequired: true }, loadComponent: () => import('./domains/client/portfolio.component').then(c => c.PortfolioComponent) },
      {
        path: 'messages',
        children: [
          { path: '', loadComponent: () => import('./domains/client/messages.component').then(c => c.MessagesComponent) },
          { path: ':id', loadComponent: () => import('./domains/client/ticket-detail.component').then(c => c.TicketDetailComponent) }
        ]
      },
      { path: 'profile', loadComponent: () => import('./domains/client/profile.component').then(c => c.ProfileComponent) },
      { path: 'email-preferences', loadComponent: () => import('./domains/client/email-preferences.component').then(c => c.EmailPreferencesComponent) },
      { path: 'trustee-dashboard', loadComponent: () => import('./domains/client/trustee-dashboard.component').then(c => c.TrusteeDashboardComponent) },
      { path: 'kyc-questionnaire', loadComponent: () => import('./domains/client/kyc-form.component').then(c => c.KycFormComponent) },
      { path: 'financial-quiz', loadComponent: () => import('./domains/client/financial-quiz.component').then(c => c.FinancialQuizComponent) },
      { path: 'kyc-pending', loadComponent: () => import('./domains/client/kyc-pending.component').then(c => c.KycPendingComponent) },
      { path: 'support', loadComponent: () => import('./domains/client/support.component').then(c => c.SupportComponent) },
    ],
  },
  {
    path: 'borrower',
    canActivateChild: [authGuard],
    data: { requiredRole: 'Borrower' },
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      { path: 'dashboard', loadComponent: () => import('./domains/borrower/borrower-dashboard.component').then(c => c.BorrowerDashboardComponent) },
      { path: 'application', loadComponent: () => import('./domains/borrower/borrower-application.component').then(c => c.BorrowerApplicationComponent) },
      { path: 'deal/:id', loadComponent: () => import('./domains/borrower/borrower-deal-detail.component').then(c => c.BorrowerDealDetailComponent) },
      {
        path: 'messages',
        children: [
          { path: '', loadComponent: () => import('./domains/client/messages.component').then(c => c.MessagesComponent) },
          { path: ':id', loadComponent: () => import('./domains/client/ticket-detail.component').then(c => c.TicketDetailComponent) }
        ]
      },
      { path: 'profile', loadComponent: () => import('./domains/borrower/borrower-profile.component').then(c => c.BorrowerProfileComponent) },
      { path: 'email-preferences', loadComponent: () => import('./domains/client/email-preferences.component').then(c => c.EmailPreferencesComponent) },
      { path: 'support', loadComponent: () => import('./domains/borrower/borrower-support.component').then(c => c.BorrowerSupportComponent) },
    ],
  },
  {
    path: 'admin',
    loadComponent: () => import('./domains/admin/admin-layout.component').then(c => c.AdminLayoutComponent),
    canActivate: [authGuard],
    data: { requiredRole: 'Admin' },
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      { path: 'dashboard', loadComponent: () => import('./domains/admin/dashboard/admin-dashboard.component').then(c => c.AdminDashboardComponent) },
      { path: 'deals', loadComponent: () => import('./domains/admin/loan-deals/loan-deal-list.component').then(c => c.LoanDealListComponent) },
      { path: 'deals/new', loadComponent: () => import('./domains/admin/loan-deals/loan-deal-form.component').then(c => c.LoanDealFormComponent) },
      { path: 'deals/:id', loadComponent: () => import('./domains/admin/loan-deals/loan-deal-detail.component').then(c => c.LoanDealDetailComponent) },
      { path: 'deals/:id/edit', loadComponent: () => import('./domains/admin/loan-deals/loan-deal-form.component').then(c => c.LoanDealFormComponent) },
      { path: 'clients', loadComponent: () => import('./domains/admin/clients/client-list.component').then(c => c.ClientListComponent) },
      {
        path: 'clients/:id',
        children: [
          { path: '', loadComponent: () => import('./domains/admin/clients/client-detail.component').then(c => c.ClientDetailComponent) },
          { path: 'account', loadComponent: () => import('./domains/admin/clients/client-account.component').then(c => c.ClientAccountComponent) }
        ]
      },
      { path: 'borrowers', loadComponent: () => import('./domains/admin/borrowers/borrower-list.component').then(c => c.BorrowerListComponent) },
      {
        path: 'borrowers/:id',
        children: [
          { path: '', loadComponent: () => import('./domains/admin/borrowers/borrower-detail.component').then(c => c.BorrowerDetailComponent) },
          { path: 'account', loadComponent: () => import('./domains/admin/borrowers/borrower-account.component').then(c => c.BorrowerAccountComponent) }
        ]
      },
      { path: 'pipeline', loadComponent: () => import('./domains/admin/pipeline/pipeline.component').then(c => c.PipelineComponent) },
      { path: 'reports', loadComponent: () => import('./domains/admin/reports/report-generator.component').then(c => c.ReportGeneratorComponent) },
      { path: 'reconciliation/verification', loadComponent: () => import('./domains/admin/reconciliation/verification-tool.component').then(c => c.VerificationToolComponent) },
      { path: 'reconciliation', loadComponent: () => import('./domains/admin/reconciliation/reconciliation.component').then(c => c.ReconciliationComponent) },
      { path: 'tickets', loadComponent: () => import('./domains/admin/tickets/ticket-list.component').then(c => c.TicketListComponent) },
      { path: 'tickets/:id', loadComponent: () => import('./domains/admin/tickets/ticket-detail.component').then(c => c.TicketDetailComponent) },
      { path: 'simulator', loadComponent: () => import('./domains/admin/simulator/simulator.component').then(c => c.SimulatorComponent) },
      { path: 'funds', loadComponent: () => import('./domains/admin/funds/funds-management.component').then(c => c.FundsManagementComponent) },
      { path: 'profile', loadComponent: () => import('./domains/admin/profile/admin-profile.component').then(c => c.AdminProfileComponent) },
      { path: 'email-test', loadComponent: () => import('./domains/admin/email-test.component').then(c => c.EmailTestComponent) },
      { path: 'email-composer', loadComponent: () => import('./domains/admin/email-composer.component').then(c => c.AdminEmailComposerComponent) },
    ],
  },
  // Public routes (no authentication required)
  {
    path: 'corporate-kyc/:token',
    loadComponent: () => import('./domains/public/public-corporate-kyc.component').then(c => c.PublicCorporateKycComponent),
  },
  {
    path: '**',
    redirectTo: '',
  },
];
